import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Row, Col, Card, Form, Badge, Table } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fmtMoney, fmtDate, STATUS_BADGE } from '../lib/format.js';

function StatCard({ icon, label, value, sub, color = 'primary', to }) {
  const inner = (
    <Card className="card-flat h-100">
      <Card.Body>
        <div className="d-flex align-items-center gap-3">
          <div className={`badge badge-soft-${color} d-flex align-items-center justify-content-center`}
               style={{ width: 44, height: 44, borderRadius: 8, fontSize: 22 }}>
            <Icon icon={icon} />
          </div>
          <div className="flex-grow-1">
            <div className="text-muted small text-uppercase" style={{ letterSpacing: '0.04em' }}>{label}</div>
            <div className="fs-4 fw-semibold">{value}</div>
            {sub && <div className="text-muted small">{sub}</div>}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
  return to ? <Link to={to} className="text-decoration-none text-reset">{inner}</Link> : inner;
}

export default function DashboardPage() {
  const auth = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const finance = useQuery({
    queryKey: ['finance-summary', year],
    queryFn: () => api.get('/finance/summary', { params: { year } }).then(r => r.data),
    enabled: auth.can('finance', 'read')
  });
  const monthly = useQuery({
    queryKey: ['finance-monthly', year],
    queryFn: () => api.get('/finance/monthly', { params: year === 'all' ? { year: 'all' } : { year } }).then(r => r.data.items),
    enabled: auth.can('finance', 'read')
  });
  const licenses = useQuery({
    queryKey: ['dashboard-licenses'],
    queryFn: () => api.get('/licenses', { params: { limit: 1000 } }).then(r => r.data.items),
    enabled: auth.can('licenses', 'read')
  });
  const recentInvoices = useQuery({
    queryKey: ['dashboard-recent-invoices'],
    queryFn: () => api.get('/invoices', { params: { limit: 8 } }).then(r => r.data.items),
    enabled: auth.can('invoices', 'read')
  });
  const recentExpenses = useQuery({
    queryKey: ['dashboard-recent-expenses', currentYear],
    queryFn: () => api.get('/expenses', { params: { limit: 8, year: currentYear } }).then(r => r.data.items),
    enabled: auth.can('expenses', 'read')
  });

  const f = finance.data || {
    income: { paid_cents: 0, outstanding_cents: 0, overdue_cents: 0, invoiced_cents: 0 },
    expenses: { gross_total_cents: 0, paid_cents: 0, open_cents: 0 },
    balance_cents: 0
  };

  const balance = f.balance_cents || 0;
  const balanceColor = balance > 0 ? 'success' : balance < 0 ? 'danger' : 'secondary';

  // Monthly chart — simple bars
  // 3 separate Balken pro Monat: Einnahmen, Spenden, Ausgaben — Skala über alle drei.
  const monthMax = Math.max(
    1,
    ...(monthly.data || []).flatMap(m => [
      m.income_paid_cents || 0,
      m.donations_cents || 0,
      m.expenses_paid_cents || 0
    ])
  );

  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);

  const activeLicenses = (licenses.data || []).filter(l => !l.revoked_at).length;
  const revokedLicenses = (licenses.data || []).filter(l => l.revoked_at).length;

  return (
    <>
      <PageHeader
        title="Übersicht"
        subtitle="Finanzlage, Lizenzstatus und neueste Aktivität"
        actions={
          <Form.Select size="sm" value={year}
            onChange={(e) => setYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}
            style={{ width: 120 }}>
            <option value="all">Gesamt</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </Form.Select>
        }
      />

      {/* ─── Finanz-KPIs (Einnahmen / Spenden / Ausgaben / Saldo) ─── */}
      {auth.can('finance', 'read') && (
        <Row className="g-3 mb-4">
          <Col md={3}>
            <StatCard
              icon="tabler:arrow-down-left"
              label={`Einnahmen ${year === 'all' ? '(gesamt)' : year}`}
              value={fmtMoney(f.income.paid_cents)}
              sub={`Offen: ${fmtMoney(f.income.outstanding_cents)}`}
              color="success"
              to={auth.can('invoices', 'read') ? '/invoices' : null}
            />
          </Col>
          <Col md={3}>
            <StatCard
              icon="tabler:gift"
              label={`Spenden ${year === 'all' ? '(gesamt)' : year}`}
              value={fmtMoney(f.income.donations_cents || 0)}
              sub={`${f.income.donations_count || 0} Eintrag${(f.income.donations_count || 0) === 1 ? '' : 'e'}`}
              color="info"
              to={auth.can('donations', 'read') ? '/donations' : null}
            />
          </Col>
          <Col md={3}>
            <StatCard
              icon="tabler:arrow-up-right"
              label={`Ausgaben ${year === 'all' ? '(gesamt)' : year}`}
              value={fmtMoney(f.expenses.paid_cents)}
              sub={`Offen: ${fmtMoney(f.expenses.open_cents)}`}
              color="warning"
              to={auth.can('expenses', 'read') ? '/expenses' : null}
            />
          </Col>
          <Col md={3}>
            <StatCard
              icon="tabler:wallet"
              label={`Saldo ${year === 'all' ? '(gesamt)' : year}`}
              value={<span className={balance >= 0 ? 'text-success' : 'text-danger'}>
                {(balance >= 0 ? '+' : '') + fmtMoney(balance)}
              </span>}
              sub={f.income.overdue_cents > 0 ? `⚠ ${fmtMoney(f.income.overdue_cents)} überfällig` : 'Bezahlt + Spenden − Ausgaben'}
              color={balanceColor}
            />
          </Col>
        </Row>
      )}

      {/* ─── Monthly bar chart (3 separate bars: Einnahmen / Spenden / Ausgaben) ─── */}
      {auth.can('finance', 'read') && monthly.data && monthly.data.length > 0 && (
        <Card className="card-flat mb-4">
          <Card.Body>
            <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
              {year === 'all'
                ? 'Verlauf seit erster Buchung'
                : `Verlauf ${year} (Jan–Dez)`}
            </h6>
            <div className="finance-bars">
              {monthly.data.map(m => {
                const incH = ((m.income_paid_cents || 0) / monthMax) * 100;
                const donH = ((m.donations_cents   || 0) / monthMax) * 100;
                const expH = ((m.expenses_paid_cents || 0) / monthMax) * 100;
                // Backend liefert bereits formatiertes Label (z.B. "Jan–Apr 25"
                // bei aggregierten Multi-Monats-Buckets in der Gesamt-Sicht).
                const label = m.label || new Date(m.month + '-01').toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
                const bucketHint = m.bucket_months > 1 ? ` (${m.bucket_months} Monate)` : '';
                const tip = `${label}${bucketHint}\nEinnahmen: ${fmtMoney(m.income_paid_cents)}\nSpenden: ${fmtMoney(m.donations_cents || 0)}\nAusgaben: ${fmtMoney(m.expenses_paid_cents)}`;
                return (
                  <div className="finance-bar-col" key={m.month} title={tip}>
                    <div className="finance-bar-stack">
                      <div className="finance-bar finance-bar-income"   style={{ height: `${incH}%` }} />
                      <div className="finance-bar finance-bar-donation" style={{ height: `${donH}%` }} />
                      <div className="finance-bar finance-bar-expense"  style={{ height: `${expH}%` }} />
                    </div>
                    <div className="finance-bar-label">{label}</div>
                  </div>
                );
              })}
            </div>
            <div className="d-flex gap-3 mt-3 small">
              <span><span className="legend-dot bg-success" /> Einnahmen</span>
              <span><span className="legend-dot bg-info" /> Spenden</span>
              <span><span className="legend-dot bg-warning" /> Ausgaben</span>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ─── License + activity ─── */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <StatCard icon="tabler:certificate" label="Aktive Lizenzen" value={activeLicenses}
            sub={revokedLicenses > 0 ? `${revokedLicenses} widerrufen` : null}
            color="primary" to={auth.can('licenses', 'read') ? '/licenses' : null} />
        </Col>
        <Col md={3}>
          <StatCard icon="tabler:building" label="Kunden"
            value={(licenses.data ? new Set(licenses.data.map(l => l.customer_id)).size : '—')}
            color="secondary" to={auth.can('customers', 'read') ? '/customers' : null} />
        </Col>
        <Col md={3}>
          <StatCard icon="tabler:file-invoice" label="Offene Rechnungen"
            value={fmtMoney(f.income.outstanding_cents)}
            color={f.income.overdue_cents > 0 ? 'warning' : 'primary'}
            to={auth.can('invoices', 'read') ? '/invoices' : null} />
        </Col>
        <Col md={3}>
          <StatCard icon="tabler:receipt" label="Offene Ausgaben"
            value={fmtMoney(f.expenses.open_cents)}
            color="warning"
            to={auth.can('expenses', 'read') ? '/expenses' : null} />
        </Col>
      </Row>

      <Row className="g-3">
        {auth.can('invoices', 'read') && (
          <Col lg={6}>
            <Card className="card-flat h-100">
              <Card.Header className="border-0 pt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Letzte Rechnungen</h6>
                  <Link to="/invoices" className="small text-decoration-none">Alle →</Link>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {recentInvoices.data && recentInvoices.data.length > 0 ? (
                  <Table hover className="mb-0 align-middle small">
                    <tbody>
                      {recentInvoices.data.map(inv => {
                        const sb = STATUS_BADGE[inv.status] || { variant: 'secondary', label: inv.status };
                        return (
                          <tr key={inv.id}>
                            <td><Link to={`/invoices/${inv.id}`} className="text-decoration-none">
                              {inv.invoice_number || <span className="text-muted">Entwurf</span>}
                            </Link></td>
                            <td><Badge bg="" className={`badge-soft-${sb.variant}`}>{sb.label}</Badge></td>
                            <td className="text-muted">{inv.issue_date ? fmtDate(inv.issue_date) : '—'}</td>
                            <td className="text-end">{fmtMoney(inv.total_cents, inv.currency)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : <div className="empty-state py-3"><div className="text-muted">Keine Rechnungen.</div></div>}
              </Card.Body>
            </Card>
          </Col>
        )}

        {auth.can('expenses', 'read') && (
          <Col lg={6}>
            <Card className="card-flat h-100">
              <Card.Header className="border-0 pt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Letzte Ausgaben</h6>
                  <Link to="/expenses" className="small text-decoration-none">Alle →</Link>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {recentExpenses.data && recentExpenses.data.length > 0 ? (
                  <Table hover className="mb-0 align-middle small">
                    <tbody>
                      {recentExpenses.data.map(e => (
                        <tr key={e.id}>
                          <td>{e.vendor}</td>
                          <td className="text-muted">{e.category_label || '—'}</td>
                          <td className="text-muted">{fmtDate(e.expense_date)}</td>
                          <td>{e.paid
                            ? <Badge bg="" className="badge-soft-success">Bezahlt</Badge>
                            : <Badge bg="" className="badge-soft-warning">Offen</Badge>}</td>
                          <td className="text-end">{fmtMoney(e.gross_cents, e.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : <div className="empty-state py-3"><div className="text-muted">Keine Ausgaben.</div></div>}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </>
  );
}
